import { useEffect, useRef } from "react";

// Full-screen celebration shown when the teaser finale (mission 999) is won:
// canvas fireworks, CRT glitch chrome, and the claim code front and center.
// Pure presentation — the win itself was already validated server-side and
// the claim code arrives inside the challenge_response text.
interface Props {
  response: string; // challenge_response (claim code already substituted)
  onClose: () => void;
}

const COLORS = ["#1FA8E0", "#00e5c7", "#ff5a5a", "#ffd75a", "#c86bff", "#e8f4fa"];

// ⚠️ NEVER put the finale's completion phrase in here. This file is shipped
// to every browser, so two of these strings ("U1984 CLEARED", "0x1984 → NULL")
// handed the teaser's answer to anyone who grepped the bundle — defeating the
// whole trace-fragment metapuzzle, whose entire premise is that the rogue DTC
// exists ONLY in the server-side completion_phrase and has to be reassembled
// from the clues. Decoration on a victory screen is not worth the puzzle.
// (Found 2026-08-08 during the answer-discovery audit.)
const DRIFT_WORDS = [
  "DTC CLEARED",
  "ECU FLUSHED",
  "MESH SEVERED",
  "GRID: HOLDING",
  "FLEET +1",
  "SIGNAL: HUMAN",
  "ROGUE PROC KILLED",
  "BUS QUIET",
];

export function TeaserVictory({ response, onClose }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const claim = /ROR-[A-Z0-9]+/.exec(response)?.[0] || "";

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    let w = (canvas.width = window.innerWidth);
    let h = (canvas.height = window.innerHeight);
    const onResize = () => {
      w = canvas.width = window.innerWidth;
      h = canvas.height = window.innerHeight;
    };
    window.addEventListener("resize", onResize);

    interface P {
      x: number;
      y: number;
      vx: number;
      vy: number;
      life: number;
      color: string;
      size: number;
    }
    const parts: P[] = [];
    const MAX_PARTS = 1400;

    const boom = (x: number, y: number, color: string) => {
      const n = 60 + Math.floor(Math.random() * 50);
      for (let i = 0; i < n && parts.length < MAX_PARTS; i++) {
        const a = Math.random() * Math.PI * 2;
        const s = 1.5 + Math.random() * 5.5;
        parts.push({
          x,
          y,
          vx: Math.cos(a) * s,
          vy: Math.sin(a) * s,
          life: 0.8 + Math.random() * 0.5,
          color,
          size: 1.5 + Math.random() * 2.5,
        });
      }
    };

    // Opening volley, then a steady barrage.
    ctx.fillStyle = "#02040a";
    ctx.fillRect(0, 0, w, h);
    for (let i = 0; i < 6; i++) {
      boom(
        w * (0.15 + 0.7 * Math.random()),
        h * (0.1 + 0.55 * Math.random()),
        COLORS[i % COLORS.length],
      );
    }

    let t = 0;
    let raf = 0;
    const tick = () => {
      t++;
      // translucent fill = phosphor trails
      ctx.fillStyle = "rgba(2,4,10,0.22)";
      ctx.fillRect(0, 0, w, h);
      if (t % 16 === 0) {
        boom(
          w * (0.08 + 0.84 * Math.random()),
          h * (0.08 + 0.6 * Math.random()),
          COLORS[Math.floor(Math.random() * COLORS.length)],
        );
      }
      for (let i = parts.length - 1; i >= 0; i--) {
        const p = parts[i];
        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.045; // gravity
        p.vx *= 0.985;
        p.vy *= 0.985;
        p.life -= 0.011;
        if (p.life <= 0) {
          parts.splice(i, 1);
          continue;
        }
        ctx.globalAlpha = Math.min(1, Math.max(0, p.life));
        ctx.fillStyle = p.color;
        ctx.fillRect(p.x, p.y, p.size, p.size);
      }
      ctx.globalAlpha = 1;
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  return (
    <div className="tv-overlay">
      <style>{TV_CSS}</style>
      <canvas ref={canvasRef} className="tv-canvas" />
      <div className="tv-scan" />
      {DRIFT_WORDS.map((word, i) => (
        <span
          key={i}
          className="tv-drift"
          style={{
            left: `${(i * 13 + 5) % 90}%`,
            animationDelay: `${i * 1.7}s`,
            animationDuration: `${9 + (i % 4) * 2}s`,
            color: COLORS[i % COLORS.length],
          }}
        >
          {word}
        </span>
      ))}
      <div className="tv-center">
        <div className="tv-title" data-text="VEHICLE RECOVERED">
          VEHICLE RECOVERED
        </div>
        <div className="tv-sub">FLEET RECOVERY +1 — THE GRID HOLDS</div>
        {claim && (
          <div className="tv-claim">
            <div className="tv-claim-label">CLAIM CODE</div>
            <div className="tv-claim-code">{claim}</div>
          </div>
        )}
        <div className="tv-body">{response}</div>
        <button className="tv-btn" onClick={onClose}>
          RETURN TO THE STREET
        </button>
      </div>
    </div>
  );
}

const TV_CSS = `
.tv-overlay{position:fixed;inset:0;z-index:3000;overflow:hidden;
  font-family:'MelGame','Courier New',monospace;background:#02040a;}
.tv-canvas{position:absolute;inset:0;}
.tv-scan{position:absolute;inset:0;pointer-events:none;opacity:0.18;
  background:repeating-linear-gradient(to bottom,rgba(255,255,255,0.10) 0 1px,
  transparent 1px 3px);animation:tv-roll 7s linear infinite;}
@keyframes tv-roll{to{background-position:0 120px;}}
.tv-drift{position:absolute;bottom:-40px;font-size:15px;letter-spacing:0.22em;
  opacity:0;animation-name:tv-rise;animation-iteration-count:infinite;
  animation-timing-function:linear;text-shadow:0 0 12px currentColor;
  pointer-events:none;white-space:nowrap;}
@keyframes tv-rise{0%{transform:translateY(0);opacity:0;}
  8%{opacity:0.85;}80%{opacity:0.5;}
  100%{transform:translateY(-110vh);opacity:0;}}
.tv-center{position:relative;z-index:2;height:100%;display:flex;
  flex-direction:column;align-items:center;justify-content:center;
  gap:16px;padding:24px;text-align:center;}
.tv-title{position:relative;font-size:clamp(34px,7vw,74px);font-weight:700;
  letter-spacing:0.12em;color:#e8f4fa;
  text-shadow:0 0 24px rgba(31,168,224,0.9),0 0 60px rgba(0,229,199,0.5);
  animation:tv-flicker 2.4s infinite;}
.tv-title::before,.tv-title::after{content:attr(data-text);position:absolute;
  left:0;top:0;width:100%;overflow:hidden;pointer-events:none;}
.tv-title::before{color:#ff5a5a;transform:translate(-3px,0);opacity:0.55;
  clip-path:inset(12% 0 58% 0);animation:tv-slice 3.1s infinite steps(1);}
.tv-title::after{color:#1FA8E0;transform:translate(3px,0);opacity:0.55;
  clip-path:inset(62% 0 8% 0);animation:tv-slice 2.3s infinite steps(1) reverse;}
@keyframes tv-slice{0%{clip-path:inset(12% 0 58% 0);}25%{clip-path:inset(48% 0 22% 0);}
  50%{clip-path:inset(78% 0 4% 0);}75%{clip-path:inset(28% 0 44% 0);}
  100%{clip-path:inset(12% 0 58% 0);}}
@keyframes tv-flicker{0%,94%,100%{opacity:1;}95%{opacity:0.55;}97%{opacity:0.9;}}
.tv-sub{font-size:clamp(13px,2.1vw,20px);letter-spacing:0.3em;color:#00e5c7;
  text-shadow:0 0 14px rgba(0,229,199,0.8);}
.tv-claim{border:1px solid #ffd75a;border-radius:8px;padding:12px 28px;
  background:rgba(20,14,2,0.75);animation:tv-pulse 1.6s ease-in-out infinite;}
@keyframes tv-pulse{0%,100%{box-shadow:0 0 14px rgba(255,215,90,0.5);}
  50%{box-shadow:0 0 44px rgba(255,215,90,0.95);}}
.tv-claim-label{font-size:13px;letter-spacing:0.4em;color:#b9a24a;margin-bottom:4px;}
.tv-claim-code{font-size:clamp(26px,4.6vw,44px);letter-spacing:0.18em;
  color:#ffd75a;text-shadow:0 0 18px rgba(255,215,90,0.9);font-weight:700;}
.tv-body{max-width:720px;max-height:26vh;overflow:auto;white-space:pre-wrap;
  font-size:16px;line-height:1.55;color:#b9cdd8;
  background:rgba(3,7,12,0.72);border:1px solid rgba(31,168,224,0.5);
  border-radius:6px;padding:14px 18px;}
.tv-btn{margin-top:4px;background:#0a1c26;border:1px solid #1FA8E0;
  color:#1FA8E0;padding:12px 26px;border-radius:6px;cursor:pointer;
  font-family:inherit;font-size:18px;letter-spacing:0.14em;}
.tv-btn:hover{background:#103040;box-shadow:0 0 18px rgba(31,168,224,0.6);}
`;

export default TeaserVictory;
